// src/lib/quizState.ts
import { QUIZ_QUESTIONS, Dimension, DIMENSION_ORDER } from '../data/quizQuestions';

export interface QuizState {
  currentIndex: number;
  answers: Record<string, number>;
}

export type QuizAction =
  | { type: 'ANSWER'; questionId: string; answerIndex: number }
  | { type: 'BACK' }
  | { type: 'RESET' };

export function initialQuizState(): QuizState {
  return { currentIndex: 0, answers: {} };
}

export function quizReducer(state: QuizState, action: QuizAction): QuizState {
  switch (action.type) {
    case 'ANSWER':
      return {
        currentIndex: Math.min(state.currentIndex + 1, QUIZ_QUESTIONS.length),
        answers: { ...state.answers, [action.questionId]: action.answerIndex },
      };
    case 'BACK':
      return { ...state, currentIndex: Math.max(0, state.currentIndex - 1) };
    case 'RESET':
      return initialQuizState();
  }
}

export type DimensionScores = Record<Dimension, number>;

export function scoreDimensions(answers: Record<string, number>): DimensionScores {
  const scores: DimensionScores = {
    systems: 0, regulatory: 0, sponsor: 0, technical: 0, scope: 0,
  };
  for (const q of QUIZ_QUESTIONS) {
    const answerIdx = answers[q.id];
    if (answerIdx === undefined) continue;
    const score = q.answers[answerIdx]?.score ?? 0;
    scores[q.dimension] += score;
  }
  return scores;
}

export type ReadinessBand = 'Strong' | 'Capable' | 'Mixed' | 'Not Yet';

export function readinessBand(totalScore: number): ReadinessBand {
  // Total possible = 10 questions × 3 = 30
  const pct = (totalScore / 30) * 100;
  if (pct >= 80) return 'Strong';
  if (pct >= 60) return 'Capable';
  if (pct >= 40) return 'Mixed';
  return 'Not Yet';
}

export interface Recommendation {
  mode: 'Plan' | 'Embed' | 'Hand-off' | 'Not yet' | 'Not a fit';
  reason: string;
}

export function recommendMode(scores: DimensionScores, scopeQ1Answer?: number): Recommendation {
  // Each dimension has 2 probes, max 6.
  const weakSponsor = scores.sponsor < 3;
  const weakSystems = scores.systems < 3;
  const weakTechnical = scores.technical < 3;

  // Sponsor is the single most blocking dimension. No sponsor, no engagement.
  if (weakSponsor && scores.sponsor < 2) {
    return {
      mode: 'Not a fit',
      reason: 'Without a named executive sponsor and an identifiable trainable owner, an embedded operator engagement cannot land. Parsant trains the team that owns the system after Hand-off, and that owner has to exist on day one.',
    };
  }

  if (weakSystems && weakTechnical) {
    return {
      mode: 'Not yet',
      reason: 'Your systems foundation and technical capacity need to mature before an embedded operator engagement makes sense. Start with one system of record and a small in-house engineering capability; come back to Parsant when those are in place.',
    };
  }

  // scope-1 (question index 8) hints at the right tier directly
  // Answer 0 = Plan, Answer 1 = Embed, Answer 2 = continuation, Answer 3 = Hand-off
  if (scopeQ1Answer === 3) {
    return {
      mode: 'Hand-off',
      reason: 'You have a working system and need it transitioned to an internal owner with documentation, runbooks, and an exit-criteria sign-off. Hand-off is the engagement.',
    };
  }

  const total = DIMENSION_ORDER.reduce((sum, d) => sum + scores[d], 0);
  const band = readinessBand(total);

  if (scopeQ1Answer === 1 || (band === 'Strong' && scopeQ1Answer !== 0)) {
    return {
      mode: 'Embed',
      reason: 'Your foundation is ready. Embed pairs a senior revenue operator with your team for 3 to 6 months to instrument the pipeline and forecast, run the weekly rhythm, and train the team that will own the work.',
    };
  }

  return {
    mode: 'Plan',
    reason: 'A 2 to 4 week Plan engagement produces the map and the roadmap, pressure-tests your foundation, and identifies what is worth committing to Embed.',
  };
}
